import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Share, Plus, Smartphone, Download, Check, ChevronRight } from "lucide-react";

/**
 * Public installation page for drivers.
 *
 * URL: /motorista/instalar (public — no auth required)
 *
 * • Android/Chrome: shows a native "Instalar App" button using the
 *   `beforeinstallprompt` event.
 * • iOS/Safari: shows Add-to-Home-Screen step-by-step instructions.
 * • Desktop: shows a QR code the driver can scan with their phone.
 */
export default function DriverInstall() {
  const [platform, setPlatform] = useState("unknown"); // ios | android | desktop | unknown
  const [installed, setInstalled] = useState(false);
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [pageUrl, setPageUrl] = useState("");

  useEffect(() => {
    // Always show the production URL in the QR (not the preview host)
    const PROD_HOST = "https://ditours.pt";
    setPageUrl(`${PROD_HOST}/motorista/instalar`);
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    const isMobile = isIOS || isAndroid || /Mobile/i.test(ua);
    setPlatform(isIOS ? "ios" : isAndroid ? "android" : isMobile ? "mobile" : "desktop");

    // Already installed (standalone display mode)?
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS specific
      // eslint-disable-next-line no-undef
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    // Chrome/Edge on Android: catch beforeinstallprompt so we can trigger it later
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPromptEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const triggerInstall = async () => {
    if (!installPromptEvt) return;
    installPromptEvt.prompt();
    const { outcome } = await installPromptEvt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallPromptEvt(null);
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#0a0f1e" }} data-testid="driver-install-page">
      {/* Header */}
      <div className="px-6 pt-10 pb-6 text-center">
        <div className="text-3xl font-display tracking-wide" style={{ color: "#f59e0b" }}>
          DI<span style={{ color: "#fff" }}>TOURS</span>
        </div>
        <div className="mt-2 text-xs tracking-[0.3em] uppercase opacity-60">Motorista</div>
      </div>

      <div className="px-6 pb-24 max-w-md mx-auto">
        {/* Installed state */}
        {installed && (
          <div className="border border-emerald-400 bg-emerald-400/10 p-6 text-center" data-testid="install-already-done">
            <Check className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
            <div className="font-display text-xl mb-2">App já instalada</div>
            <div className="text-sm opacity-70 mb-5">Pode abrir a app pelo ícone no seu ecrã principal.</div>
            <a
              href="/driver"
              className="inline-flex items-center gap-2 h-11 px-6 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide"
              data-testid="install-open-app"
            >
              Abrir app <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Not installed — Android with native prompt available */}
        {!installed && platform === "android" && installPromptEvt && (
          <div className="border-2 border-[#f59e0b] bg-[#f59e0b]/10 p-6" data-testid="install-android-prompt">
            <div className="font-display text-2xl mb-2">Instale a app</div>
            <div className="text-sm opacity-80 mb-5">
              Toque no botão abaixo e confirme "Instalar". A app aparece no seu ecrã principal
              e passa a receber viagens directamente.
            </div>
            <button
              onClick={triggerInstall}
              className="w-full h-14 bg-[#f59e0b] text-[#0a0f1e] font-bold uppercase text-sm tracking-wide flex items-center justify-center gap-2"
              data-testid="install-android-btn"
            >
              <Download className="w-5 h-5" /> Instalar DiTours
            </button>
          </div>
        )}

        {/* Android without native prompt (fallback = manual instructions) */}
        {!installed && platform === "android" && !installPromptEvt && (
          <Steps
            title="Instalar no Android"
            steps={[
              { icon: <Smartphone className="w-5 h-5" />, text: "No topo do browser, toque nos 3 pontos ⋮" },
              { icon: <Download className="w-5 h-5" />, text: "Toque em «Instalar aplicação» ou «Adicionar ao ecrã principal»" },
              { icon: <Check className="w-5 h-5" />, text: "Confirme. O ícone da DiTours aparece no ecrã principal." },
            ]}
            testId="install-android-manual"
          />
        )}

        {/* iOS instructions */}
        {!installed && platform === "ios" && (
          <Steps
            title="Instalar no iPhone"
            steps={[
              { icon: <Share className="w-5 h-5" />, text: "Toque no botão «Partilhar» (ícone quadrado ↑) na barra do Safari." },
              { icon: <Plus className="w-5 h-5" />, text: "Deslize e toque em «Adicionar ao Ecrã Principal»." },
              { icon: <Check className="w-5 h-5" />, text: "Toque em «Adicionar». O ícone DiTours fica no seu ecrã." },
            ]}
            testId="install-ios-manual"
            note="Certifique-se de que abriu esta página no Safari — a instalação não funciona no Chrome no iPhone."
          />
        )}

        {/* Desktop — show QR to hand off to phone */}
        {!installed && (platform === "desktop" || platform === "unknown") && (
          <div className="border border-white/20 bg-white/5 p-6 text-center" data-testid="install-desktop">
            <div className="font-display text-xl mb-3">Escaneie no seu telemóvel</div>
            <div className="text-sm opacity-70 mb-5">
              Abra a câmara do seu telemóvel, aponte para o código e siga o link para instalar.
            </div>
            <div className="inline-block bg-white p-3 rounded">
              {pageUrl ? (
                <QRCodeSVG value={pageUrl} size={220} level="M" />
              ) : (
                <div className="w-[220px] h-[220px] bg-white" />
              )}
            </div>
            <div className="mt-5 text-xs font-mono opacity-60 break-all">{pageUrl}</div>
          </div>
        )}

        {/* Other mobile browsers (e.g. Firefox on Android) — fallback */}
        {!installed && platform === "mobile" && (
          <Steps
            title="Instalar no seu telemóvel"
            steps={[
              { icon: <Smartphone className="w-5 h-5" />, text: "Abra o menu do seu browser (normalmente 3 pontos ou 3 barras)." },
              { icon: <Download className="w-5 h-5" />, text: "Escolha «Adicionar ao ecrã principal» ou «Instalar aplicação»." },
              { icon: <Check className="w-5 h-5" />, text: "Confirme. A DiTours passa a estar disponível no ecrã principal." },
            ]}
            testId="install-mobile-generic"
          />
        )}

        {/* Benefits section */}
        <div className="mt-10 grid grid-cols-1 gap-3">
          <Benefit title="Receba viagens directamente" body="Notificação WhatsApp com link único → 1 toque para aceitar/rejeitar." />
          <Benefit title="Waze automático" body="Ao tocar em «A caminho» ou «Viagem iniciada», o Waze abre no destino certo." />
          <Benefit title="Sem passwords" body="Login mágico por WhatsApp — sem precisar memorizar credenciais." />
        </div>

        <div className="mt-10 text-center text-xs opacity-50">
          Já tem conta? <a href="/login" className="text-[#f59e0b] underline underline-offset-4">Entrar com email</a>
        </div>
      </div>
    </div>
  );
}

function Steps({ title, steps, testId, note }) {
  return (
    <div className="border border-white/20 bg-white/5 p-6" data-testid={testId}>
      <div className="font-display text-2xl mb-5">{title}</div>
      <ol className="space-y-4">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 border border-[#f59e0b] text-[#f59e0b] flex items-center justify-center font-display">
              {i + 1}
            </div>
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2 text-sm text-white">
                {s.icon}
                <span>{s.text}</span>
              </div>
            </div>
          </li>
        ))}
      </ol>
      {note && (
        <div className="mt-5 pt-4 border-t border-white/10 text-xs opacity-70">{note}</div>
      )}
    </div>
  );
}

function Benefit({ title, body }) {
  return (
    <div className="border-l-2 border-[#f59e0b] bg-white/5 p-4">
      <div className="font-display text-sm text-[#f59e0b] uppercase tracking-wide mb-1">{title}</div>
      <div className="text-sm opacity-80">{body}</div>
    </div>
  );
}
